import React, { useState } from "react";
import { Eye, EyeOff, LockKeyhole, MessageCircleMore } from "lucide-react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import fetchData from "../../api/fetchApi";
import { getLang } from "../../i18n";

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lang = getLang();
  const isArabic = lang === "ar";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (localStorage.getItem("access_token")) {
    return <Navigate to="/admin" replace />;
  }

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetchData(
        "login",
        "POST",
        JSON.stringify({ username: username.trim(), password })
      );

      if (!response?.tokens?.access_token) {
        throw new Error(
          isArabic ? "لم يستلم التطبيق رمز دخول صالح." : "No valid access token was returned."
        );
      }

      localStorage.setItem("access_token", response.tokens.access_token);
      if (response.tokens.token) localStorage.setItem("token", response.tokens.token);
      if (response.tokens.refresh_token) {
        localStorage.setItem("refresh_token", response.tokens.refresh_token);
      }
      localStorage.setItem("user", response.tokens.username || username.trim());
      localStorage.setItem("role", response.role || "client");

      navigate("/admin", { replace: true });
    } catch (loginError) {
      setError(
        loginError.message ||
          (isArabic ? "تعذر تسجيل الدخول. حاول مرة أخرى." : "Sign in failed. Try again.")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-shell" dir={isArabic ? "rtl" : "ltr"}>
      <Link className="auth-brand" to="/" aria-label={isArabic ? "واتسي - الرئيسية" : "Whatsy home"}>
        <span className="auth-brand-mark"><MessageCircleMore size={24} /></span>
        <span>واتسي</span>
      </Link>

      <section className="auth-card" aria-labelledby="login-title">
        <div className="auth-card-copy">
          <span className="eyebrow">{isArabic ? "مساحة عملك" : "Your workspace"}</span>
          <h1 id="login-title">{isArabic ? "مرحباً بعودتك" : "Welcome back"}</h1>
          <p>
            {isArabic
              ? "ادخل لإدارة جلسة واتساب والرسائل والعملاء من مكان واحد."
              : "Manage your WhatsApp session, messages, and customers in one place."}
          </p>
        </div>

        <form onSubmit={handleLogin} className="auth-form" noValidate>
          {searchParams.get("expired") === "1" && !error && (
            <div className="notice notice-warning" role="status">
              {isArabic ? "انتهت جلستك. سجل الدخول للمتابعة." : "Your session expired. Sign in to continue."}
            </div>
          )}

          <label className="field-label" htmlFor="username">
            {isArabic ? "اسم المستخدم" : "Username"}
          </label>
          <input
            id="username"
            name="username"
            type="text"
            className="field-control"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            required
            autoComplete="username"
            autoFocus
            placeholder={isArabic ? "أدخل اسم المستخدم" : "Enter your username"}
          />

          <div className="field-heading">
            <label className="field-label" htmlFor="password">
              {isArabic ? "كلمة المرور" : "Password"}
            </label>
            <Link to="/auth/forgot-password" className="text-link">
              {isArabic ? "نسيت كلمة المرور؟" : "Forgot password?"}
            </Link>
          </div>
          <div className="password-field">
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              className="field-control"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              autoComplete="current-password"
              placeholder={isArabic ? "أدخل كلمة المرور" : "Enter your password"}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword((current) => !current)}
              aria-label={
                showPassword
                  ? isArabic ? "إخفاء كلمة المرور" : "Hide password"
                  : isArabic ? "إظهار كلمة المرور" : "Show password"
              }
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {error && <div className="notice notice-error" role="alert">{error}</div>}

          <button type="submit" className="primary-button" disabled={loading || !username.trim() || !password}>
            <LockKeyhole size={18} />
            {loading
              ? isArabic ? "جاري تسجيل الدخول…" : "Signing in…"
              : isArabic ? "تسجيل الدخول" : "Sign in"}
          </button>
        </form>

        <p className="auth-card-footer">
          {isArabic ? "ليس لديك حساب؟" : "New to Whatsy?"}{" "}
          <Link to="/auth/register" className="text-link strong">
            {isArabic ? "أنشئ حساباً" : "Create an account"}
          </Link>
        </p>
      </section>
    </div>
  );
}
